import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Autocomplete, useMediaQuery } from '@mui/material';
import { AlineAutocompleteInput } from 'src/components';
import { ZoomInIcon } from 'src/components/icons/ZoomInIcon';
import {
  useEditorTabStateActionsForTabId,
  useEditorTabStateSelectorForTabId,
} from 'src/editor/editor-tab-state-registry';
import { useCurrentTabIdOrThrow } from 'src/layouts/tabs/tab-active-context';
import { makeStyles } from 'tss-react/mui';

const ZOOM_LEVELS = [0.5, 0.75, 0.9, 1, 1.25, 1.5, 2, 5] as const;
const MIN_ZOOM = 50;
const MAX_ZOOM = 500;

interface ZoomLevelOption {
  label: string;
  value: number;
}

const useStyles = makeStyles()(theme => ({
  input: {
    // Needs !important to override MUI styles...
    minWidth: '40px !important',
    textAlign: 'center',
    width: '0 !important',
  },
  inputWrapper: {
    '& [class*="MuiAutocomplete-endAdornment"]': {
      right: '4px',
    },
    height: 32,
    minHeight: 'unset',
  },
  startAdornment: {
    height: 26,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(0.5),
    width: 26,
  },
}));

const zoomToPercentage = (zoomLevel: number): string => `${Math.round(zoomLevel * 100)}%`;

const parsePercentageToZoom = (percentage: string): number => {
  const numericValue = parseInt(percentage, 10);
  return isNaN(numericValue) ? 1 : numericValue / 100;
};

const clampZoomValue = (value: number): number => {
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, value));
};

export function ZoomLevelDropdown(): JSX.Element {
  const { classes } = useStyles();
  const tabId = useCurrentTabIdOrThrow();
  const [open, setOpen] = useState(false);
  const {
    viewer: { setViewerZoomLevel },
  } = useEditorTabStateActionsForTabId(tabId);
  const inputRef = useRef<HTMLInputElement>(null);
  const zoomLevel = useEditorTabStateSelectorForTabId(tabId, state => state.viewer.zoomLevel);
  const [inputValue, setInputValue] = useState(zoomToPercentage(zoomLevel));

  useEffect(() => {
    setInputValue(zoomToPercentage(zoomLevel));
  }, [zoomLevel]);

  const options: ZoomLevelOption[] = useMemo(
    () =>
      ZOOM_LEVELS.map(zoomLevel => ({
        label: zoomToPercentage(zoomLevel),
        value: zoomLevel,
      })),
    []
  );

  const handleInputChange = (_: React.ChangeEvent<unknown>, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const numericValue = parseInt(inputValue.replace('%', ''), 10);

      if (!isNaN(numericValue)) {
        const clampedValue = clampZoomValue(numericValue);
        const newZoomLevel = clampedValue / 100;

        setViewerZoomLevel(newZoomLevel);
        setInputValue(zoomToPercentage(newZoomLevel));
      } else {
        setInputValue(zoomToPercentage(zoomLevel));
      }

      inputRef.current?.blur();
    }
  };

  const handleChange = (_: unknown, option: ZoomLevelOption | string | null) => {
    if (option) {
      const newZoomLevel =
        typeof option === 'string' ? parsePercentageToZoom(option) : option.value;
      setViewerZoomLevel(newZoomLevel);
    }
  };

  const handleBlur = () => {
    setInputValue(zoomToPercentage(zoomLevel));
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const getOptionLabel = (option: ZoomLevelOption | string): string => {
    return typeof option === 'string' ? option : option.label;
  };

  const matchesLg = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });

  return (
    <Autocomplete
      blurOnSelect
      disableClearable
      openOnFocus
      getOptionLabel={getOptionLabel}
      inputValue={inputValue}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      open={open}
      options={options}
      renderInput={params => (
        <AlineAutocompleteInput
          {...params}
          ref={inputRef}
          classes={{ input: classes.input }}
          inputClassName={classes.inputWrapper}
          startAdornment={matchesLg ? <ZoomInIcon className={classes.startAdornment} /> : null}
          onKeyDown={handleKeyDown}
        />
      )}
      style={{ flex: '0 0 auto', width: matchesLg ? 116 : 80 }}
      value={{
        label: inputValue,
        value: parsePercentageToZoom(inputValue),
      }}
      onBlur={handleBlur}
      onChange={handleChange}
      onClose={handleClose}
      onInputChange={handleInputChange}
      onOpen={handleOpen}
    />
  );
}
